import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, type ApplicationResponse } from '../api.ts';
import { Notice, ErrorNotice, Loading, formatDate, Checkbox } from '../components/Bits.tsx';
import { Difficulties } from './Difficulties.tsx';

/** Which route each checklist entry leads to. */
const SECTION_PATHS: Record<string, string> = {
  basicinfo: '/apply/basicinfo',
  education: '/apply/schools',
  essays: '/apply/essays',
  recommendations: '/apply/recommendations',
  financial: '/apply/financial',
  verification: '/apply/verification',
  work: '/apply/jobs',
  community: '/apply/community',
  extracurricular: '/apply/extracurricular',
  awards: '/apply/awards',
};

export function useApplication() {
  return useQuery({
    queryKey: ['application'],
    queryFn: () => api.get<ApplicationResponse>('/api/application'),
  });
}

export function Dashboard() {
  const { data, isPending, error } = useApplication();
  const queryClient = useQueryClient();

  const submit = useMutation({
    mutationFn: () => api.post<{ alreadySubmitted: boolean; emailed?: boolean }>('/api/application/submit'),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['application'] }),
  });

  const setOptIn = useMutation({
    mutationFn: (optIn: boolean) => api.put('/api/application/email-preferences', { optIn }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['application'] }),
  });

  if (isPending) return <main id="main"><Loading what="Loading your application" /></main>;
  if (error) return <main id="main"><ErrorNotice error={error} /></main>;
  if (!data) return null;

  const { applicant, status, cycle } = data;
  const required = status.sections.filter((s) => s.required);
  const optional = status.sections.filter((s) => !s.required);
  const outstanding = required.filter((s) => !s.done);

  return (
    <main id="main">
      <h2>
        {applicant.firstName ? `${applicant.firstName}'s application` : 'Your application'}
      </h2>
      <p className="lede">
        {cycle && `${cycle.year} SEED Scholarship`}
        {cycle?.closesAt && !status.submitted && ` · closes ${formatDate(cycle.closesAt)}`}
      </p>

      {status.submitted ? (
        <Notice kind="ok">
          Your application was submitted{applicant.submittedAt ? ` on ${formatDate(applicant.submittedAt)}` : ''}.
          It can no longer be edited. Contact the foundation if you need to correct something.
        </Notice>
      ) : status.complete ? (
        <Notice kind="ok">Everything required is finished. You can submit whenever you are ready.</Notice>
      ) : (
        <Notice kind="info">
          {outstanding.length} of {required.length} required sections still to finish.
        </Notice>
      )}

      {submit.data?.emailed === false && (
        <Notice kind="info">
          Your application is submitted and recorded. We could not send the confirmation email,
          so do not worry if one does not arrive — nothing is wrong with your application.
        </Notice>
      )}

      <ErrorNotice error={submit.error} />
      {submit.error instanceof ApiError && submit.error.missing && (
        <Notice kind="error">Still to finish: {submit.error.missing.join(', ')}.</Notice>
      )}

      <h3>Required</h3>
      <div className="panel" style={{ padding: '0 1.25rem' }}>
        <ul className="checklist">
          {required.map((section) => (
            <li key={section.key}>
              <Link to={SECTION_PATHS[section.key] ?? '/apply'}>
                <span className={`mark ${section.done ? 'done' : ''}`} aria-hidden="true">
                  {section.done ? '✓' : ''}
                </span>
                <span className="label">{section.label}</span>
                <span className="meta">{section.done ? 'Done' : 'Not started'}</span>
              </Link>
            </li>
          ))}
        </ul>
      </div>

      <h3>Optional</h3>
      <p className="hint" style={{ marginTop: '-0.4rem' }}>
        These are not required to submit, but they are read as part of your application.
      </p>
      <div className="panel" style={{ padding: '0 1.25rem' }}>
        <ul className="checklist">
          {optional.map((section) => (
            <li key={section.key}>
              <Link to={SECTION_PATHS[section.key] ?? '/apply'}>
                <span className={`mark ${section.done ? 'done' : ''}`} aria-hidden="true">
                  {section.done ? '✓' : ''}
                </span>
                <span className="label">{section.label}</span>
                <span className="meta">{section.done ? 'Added' : 'None added'}</span>
              </Link>
            </li>
          ))}
        </ul>
      </div>

      {status.submitted && (
        <>
          <h3>Before you go</h3>
          <Difficulties year={applicant.cycleYear} />
        </>
      )}

      <h3>Email</h3>
      <div className="panel">
        <Checkbox
          checked={applicant.emailOptIn}
          disabled={setOptIn.isPending}
          onChange={(e) => setOptIn.mutate(e.target.checked)}
          label={<>
            Send me occasional news from the SEED Foundation — scholarship announcements, news updates, and more. Unsubscribe at any time.
          </>}
        />
        <p className="hint" style={{ marginTop: '0.5rem' }}>
          This is optional and has no bearing on your application submission. Whatever you choose, we will
          still email you about your own application. 
        </p>
        {applicant.unsubscribed && !applicant.emailOptIn && (
          <p className="hint">You previously unsubscribed. Ticking the box above resumes email.</p>
        )}
      </div>

      {!status.submitted && (
        <div className="actions">
          <button
            disabled={!status.complete || submit.isPending}
            onClick={() => {
              if (window.confirm(
                'Once you submit, your application is final and cannot be edited. Submit now?',
              )) submit.mutate();
            }}
          >
            {submit.isPending ? 'Submitting…' : 'Submit application'}
          </button>
          {!status.complete && (
            <span className="hint">Finish the required sections first.</span>
          )}
        </div>
      )}
    </main>
  );
}
